import React, { useEffect, useState, useContext } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import VideoModal from "../../workspace/components/VideoModal";
import { useNavigate } from "react-router-dom";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { AuthContext } from "../../shared/context/auth-context";
import ToastError from "../../shared/elements/ToastError";
import EmptyStateComponent from "./EmptyStateComponent";
import styles from "./RecentVideosSection.module.css";
import ShareButton from "../../workspace/components/ShareButton";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { FaDownload } from "react-icons/fa";
const RecentVideosSection = ({ videoData }) => {
	const { isLoading, error, sendRequest, clearError } = useHttpClient();
	const authContext = useContext(AuthContext);
	const [recentVideos, setRecentVideos] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [selectedVideo, setSelectedVideo] = useState(null);
	const navigate = useNavigate();

	useEffect(() => {
		const allVideos = (videoData || [])
			.sort((a, b) => {
				const dateA = new Date(a.createdAt || 0);
				const dateB = new Date(b.createdAt || 0);
				return dateB - dateA; // Sort in descending order (newest first)
			})
			.slice(0, 3); // Limit to 3 videos (1 row of 3)

		setRecentVideos(allVideos);
	}, [videoData]);

	const handleViewMoreVideos = () => {
		navigate("/videos");
	};

	const formatDate = (createdAt) => {
		if (!createdAt) return "";
		try {
			// Handle Firestore timestamp format
			if (createdAt._seconds) {
				// Convert Firestore timestamp to JS Date
				const date = new Date(
					createdAt._seconds * 1000 + (createdAt._nanoseconds || 0) / 1000000
				);
				return date.toLocaleDateString("en-US", {
					month: "numeric",
					day: "numeric",
					year: "numeric",
				});
			}
			// Handle regular date string
			else if (typeof createdAt === "string") {
				const date = new Date(createdAt);
				if (isNaN(date.getTime())) return "";
				return date.toLocaleDateString("en-US", {
					month: "numeric",
					day: "numeric",
					year: "numeric",
				});
			}
			// Handle Date object
			else if (createdAt instanceof Date) {
				return createdAt.toLocaleDateString("en-US", {
					month: "numeric",
					day: "numeric",
					year: "numeric",
				});
			}

			console.log("Unknown date format:", createdAt);
			return "";
		} catch (error) {
			console.error("Error formatting date:", error, "for value:", createdAt);
			return "";
		}
	};

	const handleVideoClick = async (video) => {
		try {
			const response = await sendRequest(
				`/api/videos/${video.id}/details`,
				"GET",
				null,
				{
					Authorization: "Bearer " + authContext.token,
				}
			);
			setSelectedVideo(response.data);
			setShowModal(true);
		} catch (err) {
			console.error("Error fetching video details:", err);
		}
	};

	const handleCloseModal = () => {
		setShowModal(false);
	};

	const handleDeleteVideo = async () => {
		if (!selectedVideo) return;

		try {
			await sendRequest(`/api/videos/${selectedVideo.id}`, "DELETE", null, {
				Authorization: "Bearer " + authContext.token,
			});

			// Remove the deleted video from the list
			setRecentVideos((prevVideos) =>
				prevVideos.filter((video) => video.id !== selectedVideo.id)
			);

			// Close the modal
			setShowModal(false);
		} catch (err) {
			console.error("Error deleting video:", err);
		}
	};

	const renderStatus = (status, videoUrl, publicShared) => {
		let statusElement;

		if (status === "pending") {
			statusElement = (
				<Spinner animation="border" size="sm" variant="warning" />
			);
		} else if (status === "processing") {
			statusElement = (
				<Spinner animation="border" size="sm" variant="success" />
			);
		} else if (status === "completed") {
			statusElement = (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						width: "100%",
					}}
				>
					<OverlayTrigger
						placement="top"
						delay={{ show: 250, hide: 400 }}
						overlay={
							<Tooltip>{status === "completed" ? "Download" : status}</Tooltip>
						}
					>
						<Button
							variant="link"
							style={{ padding: "5px", borderRadius: "50%" }}
							href={videoUrl}
							target="_blank"
							rel="noopener noreferrer"
							download
						>
							<FaDownload />
						</Button>
					</OverlayTrigger>
					{publicShared && (
						<ShareButton
							videoUrl={`${window.location.origin}/video?id=${selectedVideo.id}`}
							title={selectedVideo.title}
						/>
					)}
				</div>
			);
		} else if (status === "failed") {
			statusElement = (
				<span
					style={{
						display: "inline-block",
						width: "10px",
						height: "10px",
						backgroundColor: "red",
						borderRadius: "50%",
					}}
				></span>
			);
		}

		return <div style={{ display: "inline-block" }}>{statusElement}</div>;
	};

	return (
		<div className={styles.recentVideosSection}>
			<ToastError show={!!error} message={error} onClose={clearError} />
			<h3 className={styles.sectionHeader}>Recently Generated Videos</h3>

			{(isLoading && recentVideos.length === 0) ? (
				<div className="text-center py-4">
					<Spinner animation="border" role="status" />
				</div>
			) : recentVideos.length === 0 ? (
				<EmptyStateComponent type="recent" />
			) : (
				<>
					<div className={styles.videoCardsGrid}>
						{recentVideos.map((video) => (
							<Card
								key={video.id}
								className={styles.videoCard}
								onClick={() => handleVideoClick(video)}
								style={{ cursor: "pointer" }}
							>
								<div className={styles.videoCardImageContainer}>
									<div
										className={styles.videoCardImageContainerBg}
										style={{
											backgroundImage: `url(${
												video.thumbnail_url || "/placeholder-thumbnail.jpg"
											})`,
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											backgroundSize: "120%",
											backgroundPosition: "center",
											filter: "blur(12px) brightness(0.5)",
											zIndex: 0,
										}}
									></div>
									<Card.Img
										variant="top"
										src={video.thumbnail_url || "/placeholder-thumbnail.jpg"}
										alt={video.title}
										className={
											video.orientation === "vertical"
												? styles.videoCardImageVertical
												: styles.videoCardImageHorizontal
										}
										onLoad={(e) => {
											// Detect if image is horizontal or vertical
											const img = e.target;
											if (img.naturalWidth > img.naturalHeight) {
												// Horizontal image
												img.className = styles.videoCardImageHorizontal;
											} else {
												// Vertical image
												img.className = styles.videoCardImageVertical;
											}
										}}
									/>
								</div>
								<Card.Body className={styles.videoCardContent}>
									<Card.Title className={styles.videoCardTitle}>
										{video.title}
									</Card.Title>
									<Card.Text className={styles.videoCardDate}>
										Generated {formatDate(video.createdAt)}
									</Card.Text>
								</Card.Body>
							</Card>
						))}
					</div>
					<div className={styles.viewMoreContainer}>
						<Button
							variant="primary"
							className={styles.viewMoreButton}
							onClick={handleViewMoreVideos}
						>
							View More Videos
						</Button>
					</div>
				</>
			)}

			{/* Video Modal for displaying video details */}
			{selectedVideo && (
				<VideoModal
					show={showModal}
					onHide={handleCloseModal}
					video={selectedVideo}
					onDelete={handleDeleteVideo}
					renderStatus={renderStatus}
					isLoading={false}
				/>
			)}
		</div>
	);
};

export default RecentVideosSection;
